import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import EventInquiryModal from "@/components/common/EventInquiryModal";
import {
  UserCircle,
  Target,
  GraduationCap,
  Calendar,
  DollarSign,
  Palette,
  BarChart3,
  Users,
  Trophy,
  Settings,
  Bell,
  CreditCard,
  MessageCircle,
  Search,
  FileText,
  Zap,
  ChevronRight,
  Clock,
  Globe,
  Award,
  CheckCircle,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  Ticket,
  QrCode,
  Receipt,
  Building
} from "lucide-react";

const FeatureCard = ({ icon: Icon, title, features, isBlue }: {
  icon: React.ElementType;
  title: string;
  features: string[];
  isBlue?: boolean;
}) => (
  <Card className={`h-full border-l-4 border-l-secondary hover:shadow-lg transition-shadow ${
    isBlue ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
  }`}>
    <CardHeader className="pb-3">
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${isBlue ? 'bg-secondary/20' : 'bg-secondary/10'}`}>
          <Icon className={`h-6 w-6 ${isBlue ? 'text-secondary' : 'text-slate-900'}`} />
        </div>
        <div>
          <CardTitle className={`text-lg font-black ${isBlue ? 'text-white' : 'text-slate-900'}`}>{title}</CardTitle>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className={`flex items-start space-x-2 text-sm ${isBlue ? 'text-gray-300' : 'text-slate-600'}`}>
            <CheckCircle className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export default function EventManager() {
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  
  // Event Manager Features
  const allFeatures = [
    // Core Event Management
    {
      icon: Calendar,
      title: "Event Dashboard",
      category: "Core Management",
      features: [
        "Comprehensive event overview and management",
        "Real-time event status tracking",
        "Multi-event portfolio management",
        "Quick action controls and navigation",
        "Event performance metrics at a glance"
      ]
    },
    {
      icon: FileText,
      title: "Event Creation & Editing",
      category: "Event Setup",
      features: [
        "Detailed event information management",
        "Venue selection and configuration",
        "Pricing structure setup",
        "Registration form customization",
        "Event extras and merchandise management",
        "Status and visibility controls"
      ]
    },
    {
      icon: Building,
      title: "Venue Management",
      category: "Facilities",
      features: [
        "Venue database and selection",
        "Capacity and layout planning",
        "Equipment and facility requirements",
        "Location mapping and directions"
      ]
    },
    // Financial Management
    {
      icon: DollarSign,
      title: "Finance & Revenue",
      category: "Financial Management",
      features: [
        "Comprehensive revenue tracking",
        "Payment status monitoring",
        "Registration analytics and insights",
        "Financial reporting and CSV exports",
        "Profit and loss calculations",
        "Revenue optimization tools"
      ]
    },
    {
      icon: CreditCard,
      title: "Payment Processing",
      category: "Payments",
      features: [
        "Stripe integration for secure payments",
        "Multiple payment method support",
        "Automated payment confirmations",
        "Refund and chargeback management",
        "Payment analytics and reporting"
      ]
    },
    {
      icon: Receipt,
      title: "Discount & Coupons",
      category: "Pricing Strategy",
      features: [
        "Flexible discount policy management",
        "Coupon code generation and tracking",
        "Early bird and group pricing",
        "Promotional campaign management"
      ]
    },
    // Registration Management
    {
      icon: Users,
      title: "Registration Management",
      category: "Participant Management",
      features: [
        "Team and individual registration handling",
        "Custom registration forms",
        "Special request management",
        "Waitlist and capacity management",
        "Registration status tracking"
      ]
    },
    {
      icon: UserCircle,
      title: "Participant Profiles",
      category: "Attendee Management",
      features: [
        "Detailed participant information",
        "Medical and emergency contacts",
        "Skill level and category assignment",
        "Communication preferences"
      ]
    },
    // Ticketing & Access
    {
      icon: Ticket,
      title: "Ticketing System",
      category: "Access Control",
      features: [
        "Spectator ticket management",
        "Different ticket types and pricing",
        "Season passes and bulk tickets",
        "Ticket sales analytics"
      ]
    },
    {
      icon: QrCode,
      title: "QR Code & Check-in",
      category: "Event Access",
      features: [
        "QR code generation for participants",
        "Mobile check-in system",
        "Real-time attendance tracking",
        "Digital badge and credential management"
      ]
    },
    // Analytics & Reporting
    {
      icon: BarChart3,
      title: "Event Analytics",
      category: "Performance Metrics",
      features: [
        "Comprehensive event performance dashboard",
        "Registration trends and patterns",
        "Revenue analytics and forecasting",
        "Participant engagement metrics",
        "Comparative event analysis"
      ]
    },
    {
      icon: TrendingUp,
      title: "Business Intelligence",
      category: "Strategic Insights",
      features: [
        "Predictive analytics for future events",
        "Market trend analysis",
        "Customer lifetime value tracking",
        "ROI optimization recommendations"
      ]
    },
    // Communication & Marketing
    {
      icon: Mail,
      title: "Communications Hub",
      category: "Messaging",
      features: [
        "Event announcements and updates",
        "Automated email campaigns",
        "SMS notifications and alerts",
        "Push notifications for mobile users",
        "All communication history tracking"
      ]
    },
    {
      icon: Bell,
      title: "Notification System",
      category: "Alerts",
      features: [
        "Real-time event notifications",
        "Registration milestone alerts",
        "Payment and deadline reminders",
        "Emergency communication system"
      ]
    },
    // Event Configuration
    {
      icon: Settings,
      title: "Event Configuration",
      category: "Setup & Management",
      features: [
        "Event type and category management",
        "Tournament bracket generation",
        "Scoring and leaderboard systems",
        "Award and recognition setup"
      ]
    },
    {
      icon: Trophy,
      title: "Tournament Features",
      category: "Competition Management",
      features: [
        "Bracket generation and management",
        "Schedule optimization",
        "Real-time scoring updates",
        "Leaderboards and rankings",
        "Award ceremony planning"
      ]
    },
    // Documentation & Support
    {
      icon: FileText,
      title: "Documentation",
      category: "Information Management",
      features: [
        "Event packet generation",
        "Registration form management",
        "Waiver and consent forms",
        "Information packets and guides"
      ]
    },
    {
      icon: MessageCircle,
      title: "Support & FAQs",
      category: "Customer Service",
      features: [
        "Event-specific FAQ management",
        "Help desk integration",
        "Participant support tools",
        "Common issue resolution"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl font-black leading-none mb-4 tracking-tight">
            <span className="block">EVENT</span>
            <span className="block text-secondary">MANAGER.</span>
          </h1>
          <p className="text-xl md:text-2xl mb-4 text-gray-300">
            Comprehensive event management platform for organizing successful sporting events
          </p>
        </div>
      </section>

      {/* All Features - Single Section with Alternating Colors */}
      <section className="py-16 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {allFeatures.map((feature, index) => (
              <FeatureCard 
                key={index} 
                {...feature} 
                isBlue={index % 2 === 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight">
            <span className="block">READY TO MANAGE</span>
            <span className="block text-secondary">WORLD-CLASS EVENTS?</span>
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Join event organizers who trust our platform to deliver exceptional sporting experiences
          </p>
          <div className="flex justify-center">
            <Button size="lg" className="bg-secondary text-slate-900 hover:bg-secondary/90 font-black" onClick={() => setIsInquiryOpen(true)}>
              START MANAGING EVENTS
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
      
      {/* Event Inquiry Modal */}
      <EventInquiryModal
        open={isInquiryOpen}
        onOpenChange={setIsInquiryOpen}
      />
    </div>
  );
}